/**
 * Resolves /src/assets/recipes/... paths stored in DB to actual build-time URLs
 * using Vite's import.meta.glob for eager loading of all recipe images.
 */
const recipeImages = import.meta.glob<{ default: string }>(
  '/src/assets/recipes/*.webp',
  { eager: true }
);

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

/**
 * Given an image path (potentially a local asset path like /src/assets/recipes/xxx.webp),
 * returns the resolved URL usable in <img src>.
 * Handles: full URLs, local asset paths, and Supabase storage relative paths.
 */
/**
 * Builds a Supabase Storage URL, optionally using the transform API for thumbnails.
 * thumbnail=true  → /render/image/public/...?width=400&quality=60  (~40-80KB)
 * thumbnail=false → /object/public/...                              (original)
 */
const buildStorageUrl = (path: string, _thumbnail: boolean): string => {
  const bucket = path.startsWith('product-images/') ? '' : 'product-images/';
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}${path}`;
};

export const resolveRecipeImage = (
  imagePath: string | null | undefined,
  thumbnail = false,
): string | null => {
  if (!imagePath) return null;

  // Already a full Supabase storage URL — optionally switch to render endpoint
  if (imagePath.startsWith('http')) {
      return imagePath;
  }

  // Local asset path — resolve via glob map (can't transform)
  const mod = recipeImages[imagePath];
  if (mod) return mod.default;

  // Storage relative path
  if (SUPABASE_URL && !imagePath.startsWith('/src/')) {
    return buildStorageUrl(imagePath, thumbnail);
  }

  return imagePath;
};

/* ═══════════════════════════════════════════
   IMAGE GROUP → STORAGE FALLBACK
   Maps image_group keys to storage paths in the product-images bucket.
   Upload a single representative image per group and the entire
   group inherits it automatically.
   ═══════════════════════════════════════════ */

const IMAGE_GROUP_STORAGE: Record<string, string> = {
  churros:          "fallbacks/churros.webp",
  cakes:            "fallbacks/cakes.webp",
  cookies:          "fallbacks/cookies.webp",
  puddings:         "fallbacks/puddings.webp",
  jams:             "fallbacks/jams.webp",
  cinnamon_rolls:   "fallbacks/cinnamon_rolls.webp",
  breads:           "fallbacks/breads.webp",
  toppings:         "fallbacks/toppings.webp",
  cold_desserts:    "fallbacks/cold_desserts.webp",
  sweet_snacks:     "fallbacks/sweet_snacks.webp",
  brownies:         "fallbacks/brownies.webp",
  cheesecakes:      "fallbacks/cheesecakes.webp",
  gelatin:          "fallbacks/gelatin.webp",
  mousse:           "fallbacks/mousse.webp",
  truffles:         "fallbacks/truffles.webp",
  flan:             "fallbacks/flan.webp",
  panna_cotta:      "fallbacks/panna_cotta.webp",
  crumble:          "fallbacks/crumble.webp",
  ice_cream:        "fallbacks/ice_cream.webp",
  popsicles:        "fallbacks/popsicles.webp",
  energy_bars:      "fallbacks/energy_bars.webp",
  energy_balls:     "fallbacks/energy_balls.webp",
  soups:            "fallbacks/soups.webp",
  tacos:            "fallbacks/tacos.webp",
  salads:           "fallbacks/salads.webp",
  pasta:            "fallbacks/pasta.webp",
  eggs:             "fallbacks/eggs.webp",
  smoothies:        "fallbacks/smoothies.webp",
  salsas:           "fallbacks/salsas.webp",
  drinks:           "fallbacks/drinks.webp",
};

/**
 * Resolves an image_group key to a full public storage URL.
 * Returns null if the group is unknown or not mapped.
 */
export const resolveImageGroup = (
  imageGroup: string | null | undefined,
  thumbnail = false,
): string | null => {
  if (!imageGroup || !SUPABASE_URL) return null;
  const key = imageGroup.toLowerCase().trim().replace(/\s+/g, "_");
  const path = IMAGE_GROUP_STORAGE[key];
  if (!path) return null;
  return buildStorageUrl(`product-images/${path}`, thumbnail);
};

/**
 * Full recipe image resolution chain:
 * 1. recipe.image (own image — URL, local asset, or storage path)
 * 2. recipe.image_group (group fallback from storage)
 * 3. Category visual fallback (emoji + gradient, no real image)
 *
 * Returns { url, type } where type indicates the source.
 */
export const resolveRecipeImageFull = (
  image: string | null | undefined,
  imageGroup: string | null | undefined,
  category: string | null | undefined,
  thumbnail = false,
): { url: string | null; type: "own" | "group" | "category_fallback" } => {
  // 1. Own image
  const own = resolveRecipeImage(image, thumbnail);
  if (own) return { url: own, type: "own" };

  // 2. Image group
  const group = resolveImageGroup(imageGroup, thumbnail);
  if (group) return { url: group, type: "group" };

  // 3. Category fallback (no real URL — caller uses getCategoryFallback for visual)
  return { url: null, type: "category_fallback" };
};

/* ═══════════════════════════════════════════
   CATEGORY FALLBACK SYSTEM
   Premium visual fallbacks when recipe has no image.
   ═══════════════════════════════════════════ */

type CategoryFallback = {
  emoji: string;
  gradient: string; // Tailwind classes
};

const CATEGORY_FALLBACKS: Record<string, CategoryFallback> = {
  // Postres & Repostería
  postres:       { emoji: "🍰", gradient: "from-rose-200 to-pink-300" },
  reposteria:    { emoji: "🧁", gradient: "from-pink-200 to-fuchsia-300" },
  churros:       { emoji: "🥖", gradient: "from-amber-200 to-orange-300" },
  panes:         { emoji: "🍞", gradient: "from-amber-100 to-yellow-300" },
  galletas:      { emoji: "🍪", gradient: "from-amber-200 to-orange-200" },
  pasteles:      { emoji: "🎂", gradient: "from-pink-100 to-rose-300" },
  coberturas:    { emoji: "🍫", gradient: "from-amber-300 to-amber-600" },
  "coberturas_y_rellenos": { emoji: "🍫", gradient: "from-amber-300 to-amber-600" },
  "frostings_&_fillings": { emoji: "🍫", gradient: "from-amber-300 to-amber-600" },
  helados:       { emoji: "🍦", gradient: "from-sky-100 to-blue-200" },
  brownie:       { emoji: "🍫", gradient: "from-amber-600 to-amber-900" },
  brownies:      { emoji: "🍫", gradient: "from-amber-600 to-amber-900" },
  cheesecake:    { emoji: "🍰", gradient: "from-yellow-100 to-orange-200" },
  gelatina:      { emoji: "🍮", gradient: "from-fuchsia-200 to-pink-300" },
  mousse:        { emoji: "🍨", gradient: "from-rose-100 to-pink-200" },
  trufa:         { emoji: "🍬", gradient: "from-amber-400 to-amber-700" },
  trufas:        { emoji: "🍬", gradient: "from-amber-400 to-amber-700" },
  flan:          { emoji: "🍮", gradient: "from-yellow-200 to-amber-300" },
  pudin:         { emoji: "🍮", gradient: "from-orange-100 to-amber-200" },
  pudines:       { emoji: "🍮", gradient: "from-orange-100 to-amber-200" },
  panna_cotta:   { emoji: "🍨", gradient: "from-rose-50 to-pink-200" },
  crumble:       { emoji: "🥧", gradient: "from-amber-200 to-orange-300" },
  panque:        { emoji: "🍞", gradient: "from-amber-100 to-yellow-200" },
  vasito:        { emoji: "🥤", gradient: "from-pink-100 to-rose-200" },
  vasitos:       { emoji: "🥤", gradient: "from-pink-100 to-rose-200" },
  mermeladas:    { emoji: "🍓", gradient: "from-red-200 to-rose-300" },
  mermelada:     { emoji: "🍓", gradient: "from-red-200 to-rose-300" },
  bolitas:       { emoji: "🍡", gradient: "from-amber-200 to-orange-200" },
  barras:        { emoji: "🍫", gradient: "from-amber-300 to-amber-500" },
  paletas:       { emoji: "🍭", gradient: "from-pink-200 to-fuchsia-300" },
  bombones:      { emoji: "🍬", gradient: "from-amber-300 to-amber-600" },
  nieve:         { emoji: "🍧", gradient: "from-sky-100 to-cyan-200" },
  // Sopas & Caldos
  sopas:         { emoji: "🍲", gradient: "from-orange-200 to-red-300" },
  caldos:        { emoji: "🥣", gradient: "from-amber-100 to-orange-200" },
  cremas:        { emoji: "🥣", gradient: "from-yellow-100 to-amber-200" },
  // Principales
  platos_fuertes:{ emoji: "🍽️", gradient: "from-emerald-200 to-teal-300" },
  guisados:      { emoji: "🥘", gradient: "from-red-200 to-orange-300" },
  tacos:         { emoji: "🌮", gradient: "from-yellow-200 to-lime-300" },
  ensaladas:     { emoji: "🥗", gradient: "from-green-200 to-emerald-300" },
  pastas:        { emoji: "🍝", gradient: "from-yellow-200 to-amber-300" },
  picadillo:     { emoji: "🥘", gradient: "from-red-200 to-orange-200" },
  quesadillas:   { emoji: "🧀", gradient: "from-yellow-200 to-amber-300" },
  enchiladas:    { emoji: "🌯", gradient: "from-green-200 to-lime-300" },
  sopes:         { emoji: "🫓", gradient: "from-amber-200 to-orange-300" },
  tlacoyos:      { emoji: "🫓", gradient: "from-amber-100 to-yellow-300" },
  gorditas:      { emoji: "🫓", gradient: "from-amber-200 to-yellow-300" },
  molotes:       { emoji: "🫓", gradient: "from-amber-200 to-orange-200" },
  tostadas:      { emoji: "🫓", gradient: "from-yellow-200 to-amber-300" },
  tinga:         { emoji: "🌶️", gradient: "from-red-300 to-orange-400" },
  chilaquiles:   { emoji: "🌶️", gradient: "from-green-200 to-lime-300" },
  // Desayunos
  desayuno:      { emoji: "🥞", gradient: "from-amber-100 to-yellow-200" },
  desayunos:     { emoji: "🥞", gradient: "from-amber-100 to-yellow-200" },
  huevos:        { emoji: "🍳", gradient: "from-yellow-100 to-amber-200" },
  omelette:      { emoji: "🍳", gradient: "from-yellow-100 to-amber-200" },
  frittata:      { emoji: "🍳", gradient: "from-yellow-100 to-amber-200" },
  wrap:          { emoji: "🌯", gradient: "from-green-100 to-lime-200" },
  // Bebidas
  bebidas:       { emoji: "🥤", gradient: "from-sky-200 to-cyan-300" },
  smoothies:     { emoji: "🥛", gradient: "from-purple-200 to-pink-200" },
  smoothie:      { emoji: "🥛", gradient: "from-purple-200 to-pink-200" },
  jugos:         { emoji: "🧃", gradient: "from-orange-200 to-yellow-200" },
  aguas:         { emoji: "💧", gradient: "from-sky-100 to-blue-200" },
  te:            { emoji: "🍵", gradient: "from-green-100 to-emerald-200" },
  licuado:       { emoji: "🥛", gradient: "from-purple-100 to-pink-200" },
  licuados:      { emoji: "🥛", gradient: "from-purple-100 to-pink-200" },
  horchata:      { emoji: "🥛", gradient: "from-amber-50 to-amber-200" },
  chocolate_caliente: { emoji: "☕", gradient: "from-amber-300 to-amber-600" },
  cafe:          { emoji: "☕", gradient: "from-amber-200 to-amber-500" },
  limonada:      { emoji: "🍋", gradient: "from-yellow-100 to-lime-200" },
  // Snacks
  snacks:        { emoji: "🥜", gradient: "from-amber-200 to-lime-200" },
  botanas:       { emoji: "🍿", gradient: "from-yellow-200 to-amber-200" },
  chips:         { emoji: "🍟", gradient: "from-amber-200 to-yellow-200" },
  palomitas:     { emoji: "🍿", gradient: "from-yellow-100 to-amber-200" },
  rollitos:      { emoji: "🥟", gradient: "from-green-100 to-lime-200" },
  // Salsas
  salsas:        { emoji: "🫕", gradient: "from-red-300 to-orange-400" },
  salsa:         { emoji: "🫕", gradient: "from-red-300 to-orange-400" },
  guacamole:     { emoji: "🥑", gradient: "from-green-300 to-emerald-400" },
  // Almuerzo / Cena
  almuerzo:      { emoji: "🥗", gradient: "from-green-100 to-emerald-200" },
  cena:          { emoji: "🌙", gradient: "from-indigo-200 to-purple-300" },
  cenas_ligeras: { emoji: "🌙", gradient: "from-indigo-200 to-purple-300" },
  // Gelatinas
  gelatinas:     { emoji: "🍮", gradient: "from-fuchsia-200 to-pink-300" },
  // Panes & Muffins
  "breads_&_muffins": { emoji: "🍞", gradient: "from-amber-100 to-yellow-300" },
  muffins:       { emoji: "🧁", gradient: "from-amber-200 to-orange-200" },
  // Tortillas
  tortilla:      { emoji: "🫓", gradient: "from-yellow-200 to-amber-200" },
  // EN equivalents
  breakfast:     { emoji: "🥞", gradient: "from-amber-100 to-yellow-200" },
  lunch:         { emoji: "🥗", gradient: "from-green-100 to-emerald-200" },
  dinner:        { emoji: "🌙", gradient: "from-indigo-200 to-purple-300" },
  desserts:      { emoji: "🍰", gradient: "from-rose-200 to-pink-300" },
  soups:         { emoji: "🍲", gradient: "from-orange-200 to-red-300" },
  salads:        { emoji: "🥗", gradient: "from-green-200 to-emerald-300" },
  drinks:        { emoji: "🥤", gradient: "from-sky-200 to-cyan-300" },
  cookies:       { emoji: "🍪", gradient: "from-amber-200 to-orange-200" },
  ice_cream:     { emoji: "🍦", gradient: "from-sky-100 to-blue-200" },
  jams:          { emoji: "🍓", gradient: "from-red-200 to-rose-300" },
  bread:         { emoji: "🍞", gradient: "from-amber-100 to-yellow-300" },
  pasta:         { emoji: "🍝", gradient: "from-yellow-200 to-amber-300" },
};

const DEFAULT_FALLBACK: CategoryFallback = {
  emoji: "🍽️",
  gradient: "from-slate-200 to-slate-300",
};

/**
 * Returns a premium fallback visual config for a given category.
 * Normalizes category name (lowercase, trimmed, accents stripped).
 */
export const getCategoryFallback = (category: string | null | undefined): CategoryFallback => {
  if (!category) return DEFAULT_FALLBACK;
  const key = category
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_");
  return CATEGORY_FALLBACKS[key] || DEFAULT_FALLBACK;
};

/** List of all available image_group keys for UI dropdowns */
export const IMAGE_GROUP_KEYS = Object.keys(IMAGE_GROUP_STORAGE).sort();

/**
 * Canonical check: does a recipe have a real photo (own or group)?
 * Returns false for category_fallback (emoji + gradient only).
 * Use this everywhere instead of local hasRealImage copies.
 */
export const hasRealImage = (recipe: {
  image?: string | null;
  image_group?: string | null;
  category?: string | null;
}): boolean => {
  const { type } = resolveRecipeImageFull(recipe.image, recipe.image_group, recipe.category);
  return type === "own" || type === "group";
};
